import { useEffect, useState } from 'react'

import { DataTable, FullScreenContainer, useLayout } from '@/components/ui'
import type { Table } from '@/components/ui/table/types'
import {
  type Transaction,
  TransactionsBulkActions,
  TransactionsFilters,
  type TransactionsTableVariant,
  transactionsColumns,
} from '@/modules/projects'
import {
  type FieldSchema,
  useFilters,
  useSearchQuery,
  useSubmittedDialog,
} from '@/stores'
import { cn } from '@/utils'

interface TransactionsTableProps {
  transactions: Transaction[]
  filterSchema: FieldSchema[]
  paginationKey: string
  dialogId: string
  exportFilename: string
  emptyMessage?: string
  variant?: TransactionsTableVariant
}

// Shared ledger surface for both the statement-side Transactions view and the
// Checks view. The data source and per-view identifiers (paginationKey,
// dialogId, exportFilename, emptyMessage) are passed by the parent so this
// component stays a pure presenter.
export const TransactionsTable = ({
  transactions,
  filterSchema,
  paginationKey,
  dialogId,
  exportFilename,
  emptyMessage,
  variant = 'transactions',
}: TransactionsTableProps) => {
  const [table, setTable] = useState<Table<Transaction> | null>(null)

  const hasTransactions = transactions.length > 0
  const { isFullScreen, setIsFullScreen } = useLayout()
  const submittedDialog = useSubmittedDialog()
  const filters = useFilters()
  const searchQuery = useSearchQuery()
  const hasShareNumber = transactions.some(
    (transaction) => transaction.shareNumber,
  )

  useEffect(() => {
    if (submittedDialog && table) {
      table.resetRowSelection()
    }
  }, [submittedDialog, table])

  return (
    <FullScreenContainer className="flex-1 min-h-0 flex flex-col gap-3">
      <TransactionsFilters
        table={table}
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
        hasShareNumber={hasShareNumber}
        dialogId={dialogId}
        exportFilename={exportFilename}
      />
      <div
        className={cn(
          'flex-1 min-h-[500px] flex flex-col rounded-lg border bg-white overflow-auto',
          !isFullScreen && 'max-h-[60vh]',
        )}
      >
        <DataTable
          columns={transactionsColumns(hasShareNumber, variant)}
          data={transactions}
          height={hasTransactions ? 'flex-1' : 'flex-none'}
          onTableReady={setTable}
          paginationKey={paginationKey}
          filters={filters}
          filterSchema={filterSchema}
          globalFilter={searchQuery}
          emptyMessage={emptyMessage}
          scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
          bulkActions={(selection) => (
            <TransactionsBulkActions
              selection={selection}
              transactions={transactions}
              variant={variant}
            />
          )}
        />
      </div>
    </FullScreenContainer>
  )
}
